<template>
  <div :ref="formRef"
       class="person--form">

    <v-row :class="$vuetify.breakpoint.smAndUp ? 'space-between' : ''"
           no-gutters>

      <!-- Herr / Frau mit Titel -->
      <v-col cols="12"
             sm="11">
        <v-radio-group v-model="anrede"
                       v-validate="'required'"
                       :error-messages="errors.collect('anrede')"
                       data-vv-as="Anrede"
                       data-vv-name="anrede"
                       name="anrede"
                       class="no-errors-group">
          <v-row no-gutters>
            <v-radio label="Frau"
                     value="F"
                     color="primary" />
            <v-radio label="Herr"
                     value="H"
                     color="primary" />
            <v-radio label="keine Angabe"
                     value="kA"
                     color="primary" />
          </v-row>
        </v-radio-group>
      </v-col><v-col cols="0" sm="1"/>

      <v-col cols="12"
             sm="6">
        <v-text-field v-model="titel"
                      name="titel"
                      label="Titel" />
      </v-col><v-col cols="0" sm="6"/>

      <v-col cols="12"
             sm="6">
        <v-text-field v-model.trim="firstname"
                      v-validate="{ required: true, regex: /[^0-9!§$%&\/()=?#*+{\[\]}]{1,}$/ }"
                      :error-messages="errors.collect('firstname')"
                      data-vv-as="Vorname"
                      class="required"
                      name="firstname"
                      label="Vorname" />
      </v-col>

      <v-col cols="12"
             sm="6">
        <v-text-field v-model.trim="lastname"
                      v-validate="{ required: true, regex: /[^0-9!§$%&\/()=?#*+{\[\]}]{1,}$/ }"
                      :error-messages="errors.collect('lastname')"
                      data-vv-as="Nachname"
                      class="required"
                      name="lastname"
                      label="Nachname" />
      </v-col>

      <template v-if="isUsed('geburt') || isUsed('staat')">

        <v-col v-if="isUsed('geburt')"
               cols="12"
               sm="6">
          <date-field v-model="geburt"
                      v-validate="mindestalter ?
                          {required: true, date_format: ['DD.MM.YYYY'], before: [maxGeburt, true]} :
                          {required: true, date_format: ['DD.MM.YYYY']}"
                      :error-messages="errors.collect('geburt')"
                      :placeholder="geburt"
                      :disabled="isPksSecuro"
                      data-vv-as="Geburtsdatum"
                      class="required"
                      hint="tt.mm.jjjj"
                      name="geburt"
                      label="Geburtsdatum" />
        </v-col>

        <v-col v-if="isUsed('staat')"
               cols="12"
               sm="6">
          <v-autocomplete v-model="staat"
                          v-validate="'required'"
                          :error-messages="errors.collect('staat')"
                          :items="countryList"
                          auto-select-first
                          data-vv-as="Staatsangehörigkeit Land"
                          data-vv-name="staat"
                          class="required"
                          name="staat"
                          label="Staatsangehörigkeit Land" />
        </v-col>

        <v-col v-if="isUsed('geburt') && !isUsed('staat') || !isUsed('geburt') && isUsed('staat')"
               cols="0" sm="6"/>

      </template>

      <template v-if="isUsed('strasse')">

        <v-col cols="12"
               sm="6">
          <v-text-field v-model="strasse"
                        v-validate="{ required: true, regex: /^[^!§$%&\/()=?#*+{\[\]}]*$/ }"
                        :error-messages="errors.collect('strasse')"
                        data-vv-as="Straße"
                        class="required"
                        name="strasse"
                        label="Straße" />
        </v-col>

        <v-col cols="12"
               sm="6">
          <v-text-field v-model="hausnummer"
                        v-validate="{ required: true, regex: /^[1-9][0-9a-zA-Z-\/,]*$/ }"
                        :error-messages="errors.collect('hausnummer')"
                        data-vv-as="Hausnummer"
                        class="required"
                        name="hausnummer"
                        label="Hausnummer"
                        @change="restrictHausnummer"
                         />
        </v-col>

        <v-col cols="12"
               sm="6">
          <v-text-field v-model="adresszusatz"
                        data-vv-as="Adresszusatz"
                        name="adresszusatz"
                        label="Adresszusatz" />
        </v-col><v-col cols="0" sm="6"/>

      </template>

      <!-- PLZ und Ort -->
      <template v-if="isUsed('plz')">

        <v-col cols="12"
               sm="6">
          <v-text-field v-model="plz"
                        v-validate="{ required: true, regex: /^[0-9]{5} ?$/ }"
                        :disabled="isPksSecuro"
                        :error-messages="errors.collect('plz')"
                        data-vv-as="Postleitzahl"
                        class="required"
                        name="plz"
                        label="Postleitzahl"
                        @input="onInputPlz" />
        </v-col>

        <v-col cols="12"
               sm="6">
          <v-text-field v-if="cityList.length === 0"
                        v-model="ort"
                        v-validate="{ required: true, regex: regex.ort }"
                        :disabled="isPksSecuro"
                        :error-messages="errors.collect('ort')"
                        data-vv-as="Ort"
                        class="required"
                        name="ort"
                        label="Ort"
                        hint="Hauptwohnsitz in Deutschland" />
          <v-combobox v-else
                      v-model="ort"
                      v-validate="{ required: true, regex: regex.ort }"
                      :error-messages="errors.collect('ort')"
                      :items="cityList"
                      data-vv-as="Ort"
                      name="ort"
                      class="required"
                      label="Ort"
                      hint="Hauptwohnsitz in Deutschland" />
        </v-col>

      </template>

      <!-- Beruf und Info -->
      <template v-if="isUsed('beruf')">

        <v-col cols="12"
               sm="6" style="overflow-x: hidden;">
          <v-combobox v-model="beruf"
                      v-validate="{ required: true, regex: /^[^0-9!§$%&=?#*+{\[\]}]*$/ }"
                      :rules="[(beruf !== '' || 'Beruf ist ein Pflichtfeld')]"
                      :error-messages="errors.collect('beruf')"
                      :items="berufe"
                      class="required"
                      data-vv-as="Beruf"
                      data-vv-name="beruf"
                      label="Beruf"
                      hint="Bitte Beruf eingeben oder auswählen" />
        </v-col><v-col cols="0" sm="6"/>

      </template>


      <!-- Telefon und Mobil -->
      <template v-if="isUsed('telefon') || isUsed('mobil')">

        <v-col v-if="isUsed('telefon')"
               cols="12"
               sm="6">
          <v-text-field v-model="telefon"
                        v-validate="{ regex: regex.telefon }"
                        :error-messages="errors.collect('telefon')"
                        data-vv-as="Festnetz- / Mobilnummer"
                        name="telefon"
                        label="Festnetz- / Mobilnummer" />
        </v-col>

        <v-col v-if="isUsed('mobil')"
               cols="12"
               sm="6">
          <v-text-field v-model="mobil"
                        v-validate="{ regex: regex.telefon }"
                        :error-messages="errors.collect('mobil')"
                        data-vv-as="Festnetz- / Mobilnummer"
                        name="mobil"
                        label="Alternative Festnetz- / Mobilnummer" />
        </v-col>

      </template>

      <!-- E-Mail Adresse -->
      <template v-if="isUsed('email')">
        <v-col cols="12"
               sm="6">
          <v-text-field v-model="email"
                        v-validate="yoligValidation"
                        :error-messages="errors.collect('emailyolig')"
                        data-vv-as="E-Mail Adresse"
                        :class="yoligRequired"
                        :hint="yoligAntrag ? 'E-Mail Adresse ist ein Pflichtfeld, papierlose Bearbeitung und Kommunikation wird bevorzugt' : ''"
                        name="emailyolig"
                        label="E-Mail" />
        </v-col>
      </template>

    </v-row>

  </div>
</template>

<script>
import KeyTranslator from "@/components/helper/keyTranslatorMixin";
import ValidationMixin from "@/components/helper/ValidationMixin";
import {mapTwoWay} from "@/store/twowayBindingMixin";
import twowayBinding from "@/store/twowayBindingMixin";
import Proffession from "@/store/config/proffessions";
import countries from "@/store/config/countries";
import DateField from "@/components/DateField";
import plzOrtMixin from "@/components/helper/plzOrtMixin";
import REGEX from "@/validation/regexes";

export default {
  components: { DateField },
  mixins: [ValidationMixin, KeyTranslator, twowayBinding, plzOrtMixin],
  props: {
    formRef: {
      type: String,
      required: true
    }
  },
  data: function () {
    return {
      berufe: Proffession.jobs,
      countryList: countries.countryList,
      regex: REGEX
    };
  },
  computed: {
    isPksSecuro() {
      return this.statePath.split('.')[1] === 'vn' && /securo/.test(this.$store.getters.activeTarif);
    },
    mindestalter() {
      return this.statePath.split('.')[1] === 'vn' || this.statePath.split('.')[1] === 'kontoinhaber';
    },
    maxGeburt() {
      const now = new Date();
      let day = (now.getDate() < 10 ? '0' : '') + now.getDate();
      let month = ((now.getMonth() + 1) < 10 ? '0' : '') + (now.getMonth() + 1);
      const year = now.getFullYear() - 18;
      if(day == '29' && month == '02') day = '28';

      return `${day}.${month}.${year}`;
    },
    ...mapTwoWay([
      "anrede",
      "titel",
      "firstname",
      "lastname",
      "strasse",
      "hausnummer",
      "adresszusatz",
      "plz",
      "ort",
      "geburt",
      "beruf",
      "email",
      "telefon",
      "mobil",
      "staat"
    ]),
    yoligValidation() {
      let requ = (this.$store.state.b.productLine == 'YOLIG') ? true : false;
      return { required: requ, email: true };
    },
    yoligRequired() {
      let requ = (this.$store.state.b.productLine == 'YOLIG') ? true : false;
      return { required: requ };
    },
    yoligAntrag() {
      return (this.$store.state.b.productLine == 'YOLIG') ? true : false;
    }
  },
  methods: {
    isUsed(field) {
      return !(this.stateRef.unused && this.stateRef.unused.includes(field));
    },
    restrictHausnummer() { 
      this.hausnummer = this.hausnummer.replace(/[^0-9a-zA-Z-/,]/g, "");//on blur change
    }
  }
};
</script>

<style>
.person--form {
  margin-bottom: -16px !important;
  overflow: auto;
}

.person--form .v-icon {
  cursor: pointer;
}

</style>

